import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useGetDevicesLocationsQuery } from "../../../Api/apiSlice";
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import { useMap } from 'react-leaflet';
// Fix marker icons for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});
const MAPBOX_TOKEN =
  "pk.eyJ1Ijoicml6d2Fub3QiLCJhIjoiY20wYzkwZHlyMDBtdDJrcHV0ZW1kbjA4ayJ9.rqt53odFTEC-GL7U81mVGw";
const TileLayerSwitcher = ({ tileType, setTileType }) => (
  <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, background: 'white', borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,0.2)', padding: 4 }}>
    <button onClick={() => setTileType('streets')} style={{ marginRight: 4, background: tileType === 'streets' ? '#1976d2' : '#eee', color: tileType === 'streets' ? '#fff' : '#333', border: 'none', borderRadius: 2, padding: '4px 8px', cursor: 'pointer' }}>Streets</button>
    <button onClick={() => setTileType('satellite')} style={{ background: tileType === 'satellite' ? '#1976d2' : '#eee', color: tileType === 'satellite' ? '#fff' : '#333', border: 'none', borderRadius: 2, padding: '4px 8px', cursor: 'pointer' }}>Aerial</button>
  </div>
);

function MarkerWithFocus({ marker, onFocus, animatedMarkerId, setAnimatedMarkerId }) {
  const map = useMap();
  const markerRef = useRef();
  useEffect(() => {
    if (animatedMarkerId === marker.id && markerRef.current) {
      const icon = markerRef.current._icon;
      if (icon) {
        icon.classList.add('marker-bounce');
        setTimeout(() => {
          icon.classList.remove('marker-bounce');
          setAnimatedMarkerId(null);
        }, 700); // duration of bounce
      }
    }
  }, [animatedMarkerId, marker.id, setAnimatedMarkerId]);
  return (
    <Marker
      key={marker.id}
      position={marker.position}
      icon={marker?.icon}
      ref={markerRef}
      eventHandlers={{
        click: () => {
          map.setView(marker.position, 17, { animate: true });
          setAnimatedMarkerId(marker.id);
          if (onFocus) onFocus(marker);
        },
      }}
    >
      <Popup>{marker.label}</Popup>
    </Marker>
  );
}
const RealTimeMapWithLines = ({click, setClick, focusOnLatest, setFocusOnLatest}) => {
  const { data, error, isLoading, refetch } = useGetDevicesLocationsQuery();
  const [markers, setMarkers] = useState([]);
  const [lines, setLines] = useState([]);
  const defaultPosition = [33.6071, 73.1006]; // Default map center
  const greenIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png", // Green marker
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
  
  const redIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png", // Red marker
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
  useEffect(() => {
    if (data?.devices) {
      const deviceMarkers = [];
      const deviceLines = [];
      data.devices.forEach((device) => {
        const { deviceModel, deviceId, status, path } = device;
        if (status !== 'active' || !path.length) return; // Only show active devices with path
        // Start and end markers
        const startCoordinates = [parseFloat(path[0].latitude), parseFloat(path[0].longitude)];
        const endCoordinates = [parseFloat(path[path.length-1].latitude), parseFloat(path[path.length-1].longitude)];
        deviceMarkers.push(
          {
            id: `${deviceId}-start`,
            position: startCoordinates,
            label: `${deviceModel} (Start)` ,
            icon: greenIcon
          },
          {
            id: `${deviceId}-end`,
            position: endCoordinates,
            label: `${deviceModel} (End)` ,
            icon: redIcon
          }
        );
        // Full path polyline
        const polyline = path.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);
        deviceLines.push(polyline);
      });
      setMarkers(deviceMarkers);
      setLines(deviceLines);
    }
  }, [data, click]);

  useEffect(() => {
    refetch();
  }, [click]);

  // Auto-refetch every 3 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 3000);
    return () => clearInterval(interval);
  }, [refetch]);

  const [tileType, setTileType] = useState('streets');
  const [animatedMarkerId, setAnimatedMarkerId] = useState(null);
  const mapRef = useRef();

  useEffect(() => {
    if (focusOnLatest && markers.length > 0 && mapRef.current) {
      // Add a small delay to ensure map and markers are rendered
      const latestMarker = markers[markers.length - 1];
      const timer = setTimeout(() => {
        mapRef.current.setView(latestMarker.position, 17, { animate: true });
        setAnimatedMarkerId(latestMarker.id);
        if (setFocusOnLatest) setFocusOnLatest(false);
      }, 300); // 300ms delay
      return () => clearTimeout(timer);
    }
  }, [focusOnLatest, markers, setFocusOnLatest]);

  return (
    <div style={{ height: "90%", width: "100%", marginTop: "20px", position: 'relative' }}>
      <style>{`
        .marker-bounce {
          animation: marker-bounce 0.7s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes marker-bounce {
          0%   { transform: translateY(0); }
          20%  { transform: translateY(-20px); }
          40%  { transform: translateY(-30px); }
          50%  { transform: translateY(-15px); }
          60%  { transform: translateY(-30px); }
          80%  { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
      `}</style>
      {isLoading && <div>Loading map...</div>}
      {error && <div style={{textAlign:"center"}}>No device movement detected.</div>}
      {!isLoading && !error && (
        <MapContainer
          center={defaultPosition}
          zoom={6}
          style={{ width: "100%", height: "100%" }}
          whenCreated={mapInstance => { mapRef.current = mapInstance; }}
        >
          <TileLayer
            url={tileType === 'streets'
              ? "https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN
              : "https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {/* Render markers */}
          {markers.map((marker) => (
            <MarkerWithFocus key={marker.id} marker={marker} animatedMarkerId={animatedMarkerId} setAnimatedMarkerId={setAnimatedMarkerId} />
          ))}
          {/* Render lines */}
          {lines.map((line, index) => (
            <Polyline
              key={index}
              positions={line}
              pathOptions={{ color: "red", weight: 4 }}
            />
          ))}
          {/* Tile Layer Switcher positioned inside the map */}
          <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, background: 'white', borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,0.2)', padding: 4 }}>
            <button onClick={() => setTileType('streets')} style={{ marginRight: 4, background: tileType === 'streets' ? '#1976d2' : '#eee', color: tileType === 'streets' ? '#fff' : '#333', border: 'none', borderRadius: 2, padding: '4px 8px', cursor: 'pointer' }}>Streets</button>
            <button onClick={() => setTileType('satellite')} style={{ background: tileType === 'satellite' ? '#1976d2' : '#eee', color: tileType === 'satellite' ? '#fff' : '#333', border: 'none', borderRadius: 2, padding: '4px 8px', cursor: 'pointer' }}>Aerial</button>
          </div>
        </MapContainer>
      )}
    </div>
  );
};

export default RealTimeMapWithLines;
